import { WorkerRequirementDomain } from "@clipboard-health/contract-worker-app-bff";
import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { useFacilityProfile } from "@src/appV2/Facilities/api/useFacilityProfile";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { useRef } from "react";
import { useParams } from "react-router-dom";

import { ListLoadingState } from "../../components/ListLoadingState";
import { PageWrapper } from "../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../components/StickyScrollPageHeader";
import { PullToRefresh } from "../../ShiftDiscovery/ListView/PullToRefresh";
import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { useGetRequirementsForWorkplace } from "../api/wrappers/useGetRequirementsForWorkplace";
import { DocumentCardsWrapper } from "../components/DocumentCardsWrapper";
import { DocumentsPageDocumentCards } from "../DocumentsPage/DocumentCards";
import { DocumentsErrorPage } from "../ErrorPage";
import { type WorkplaceDocumentsPageParams } from "../types";
import { useCredentialRequirementConfiguration } from "../useCredentialRequirementConfiguration";
import { useGetWorkerLicensesAllowedForDocuments } from "../useGetWorkerLicensesAllowedForDocuments";

interface WorkplaceDocumentsSearchParams extends Record<string, unknown> {
  qualification?: string;
  domain?: WorkerRequirementDomain;
  includeCredentials?: string;
}

export function WorkplaceDocumentsPage() {
  const { workplaceId } = useParams<WorkplaceDocumentsPageParams>();
  const {
    qualification,
    domain = WorkerRequirementDomain.FLEX,
    includeCredentials,
  } = useSearchParams<WorkplaceDocumentsSearchParams>();

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  const { licenses: availableLicenses } = useGetWorkerLicensesAllowedForDocuments();

  const stackRef = useRef<HTMLDivElement>(null);
  useScrollToTopOnStatusTap(stackRef);

  const {
    data: workplaceProfile,
    isLoading: isWorkplaceProfileLoading,
    isError: isWorkplaceProfileError,
  } = useFacilityProfile({
    workplaceId,
  });

  const {
    data: workerRequirements,
    isLoading: isWorkerRequirementsLoading,
    isError: isWorkerRequirementsError,
    refetch,
  } = useGetRequirementsForWorkplace({
    qualifications: qualification ? [qualification] : availableLicenses,
    domain,
    workplaceId,
  });

  useLogEffect(APP_V2_USER_EVENTS.VIEWED_WORKPLACE_DOCUMENTS, {
    workplace_id: workplaceId,
    qualification,
  });

  if (isWorkplaceProfileLoading || isWorkerRequirementsLoading) {
    return <ListLoadingState texts={["Loading workplace documents..."]} />;
  }

  if (isWorkplaceProfileError || isWorkerRequirementsError) {
    return <DocumentsErrorPage title="Error loading documents" />;
  }

  const filteredWorkerRequirements =
    includeCredentials === "true"
      ? workerRequirements
      : workerRequirements?.filter(
          (workerRequirement) =>
            !credentialRequirementConfiguration.has(workerRequirement.requirementId)
        );

  return (
    <PageWrapper>
      <StickyScrollPageHeader
        shouldWrapTitle
        title={workplaceProfile?.name}
        scrollContainerRef={stackRef}
      />
      {isDefined(qualification) && (
        <Box sx={{ paddingX: 5, paddingBottom: 4 }}>
          <Text>
            Showing all required documents for {qualification}{" "}
            {domain === WorkerRequirementDomain.FLEX ? "shifts" : "jobs"} at this workplace
          </Text>
        </Box>
      )}

      <PullToRefresh
        scrollRef={stackRef}
        onRefresh={async () => {
          await refetch();
        }}
      />

      <DocumentCardsWrapper ref={stackRef} sx={{ padding: 5 }}>
        <DocumentsPageDocumentCards documents={filteredWorkerRequirements} />
      </DocumentCardsWrapper>
    </PageWrapper>
  );
}
